import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { applyNodeChanges, type NodeChange } from "@xyflow/react";
import { useGetServiceOrderInit } from "../../hooks/ServiceOrder/useGetServiceOrderInit";
import { useGetServiceOrderHeaderComparison } from "../../hooks/ServiceOrder/useGetServiceOrderHeaderComparison";
import { useGetCustomerPaymentsByCoOrderD365 } from "../../hooks/CustomerPayment/D365/useGetCustomerPaymentsByCoOrderD365";
import { useGetSalesOrderHeaderStatusD365 } from "../../hooks/SalesOrder/D365/useGetSalesOrderHeaderStatusD365";
import { useGetCustomerComparison } from "../../hooks/Customer/useGetCustomerComparison";
import { useGetVatNumComparison } from "../../hooks/Tax/useGetVatNumComparison";
import { useGetSalesOrderLinesD365 } from "../../hooks/SalesOrderLine/D365/useGetSalesOrderLinesD365";
import { useGetServiceOrderLineExistence, type IServiceOrderLineExistenceInfo } from "../../hooks/ServiceOrder/useGetServiceOrderLineExistence";
import { getReleasedProductComparison } from "../../hooks/ReleasedProduct/useGetReleasedProductComparison";
import { getProductComparison } from "../../hooks/Product/useGetProductComparison";
import { getProductD365, type IProductD365 } from "../../hooks/Product/useGetProductD365";
import { getReleasedProductD365, type IReleasedProductD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductD365";
import { useExplorerStore } from "../../stores/useExplorerStore";
import { useShallow } from "zustand/react/shallow";
import { applyDagreLayout } from "./useAutoLayout";
import {
    buildComparisonQueryMap,
    buildD365QueryMap,
    computeStatus,
    isValidTaxExemptNumber,
} from "./graphHelpers";
import type { IComparisonInfo } from "../../declarations/types";
import type {
    ExplorerNode,
    ExplorerEdge,
    ExplorerNodeStatus,
} from "../../declarations/IExplorerTypes";
import type { IReadVerbPayload } from "../../declarations/IReadVerbPayload";

export function useServiceOrderGraph(orderID: number) {
    const selectedNodeId = useExplorerStore((s) => s.selectedNodeId);
    const setSelectedNodeId = useExplorerStore((s) => s.setSelectedNodeId);
    const setResolvedKeys = useExplorerStore((s) => s.setResolvedKeys);
    const {
        expandedServiceLineKeys,
        expandedServiceLineGroups,
        expandedReleasedProducts,
    } = useExplorerStore(useShallow((s) => ({
        expandedServiceLineKeys: s.expandedServiceLineKeys,
        expandedServiceLineGroups: s.expandedServiceLineGroups,
        expandedReleasedProducts: s.expandedReleasedProducts,
    })));
    const toggleServiceLineExpanded = useExplorerStore((s) => s.toggleServiceLineExpanded);
    const toggleServiceLineGroupExpanded = useExplorerStore((s) => s.toggleServiceLineGroupExpanded);

    // Fetch init data (resolves CS keys for the order)
    const { data: init, isLoading: initLoading } = useGetServiceOrderInit(orderID);

    // Resolved keys live in the store (populated by the useEffect below). Reading from
    // the store rather than `init` directly so the field names match IExplorerResolvedKeys
    // (e.g. salesOrderNumber rather than coServiceOrderID).
    const resolvedKeys = useExplorerStore((s) => s.resolvedKeys);

    const headerComparisonPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            salesOrderNumber: resolvedKeys?.salesOrderNumber ?? "",
            serviceOrderID: orderID,
        },
    }), [resolvedKeys?.dataAreaId, resolvedKeys?.salesOrderNumber, orderID]);

    const headerComparison = useGetServiceOrderHeaderComparison(headerComparisonPayload);
    const { data: salesOrderStatus } = useGetSalesOrderHeaderStatusD365(headerComparisonPayload);

    // Customer Payments — D365-only branch keyed by CoServiceOrderID (== SheaSalesId in D365)
    const customerPaymentsQuery = useGetCustomerPaymentsByCoOrderD365(init?.coServiceOrderID);

    // Customer comparison — uses salesAccID + custAccount from init
    const customerPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            customerAccount: resolvedKeys?.custAccount ?? "",
            salesAccID: resolvedKeys?.salesAccID ?? 0,
        },
    }), [resolvedKeys?.dataAreaId, resolvedKeys?.custAccount, resolvedKeys?.salesAccID]);
    const customerComparison = useGetCustomerComparison(customerPayload);

    // VAT Number comparison
    const taxExemptNumber = resolvedKeys?.taxExemptNumber ?? "";
    const taxCertCountryRegionId = resolvedKeys?.taxCertCountryRegionId ?? "";
    const hasValidVatNum = isValidTaxExemptNumber(taxExemptNumber);

    const vatNumPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            countryRegionId: taxCertCountryRegionId,
            vatNum: taxExemptNumber,
            salesAccID: resolvedKeys?.salesAccTaxCertID ?? 0,
        },
    }), [resolvedKeys?.dataAreaId, resolvedKeys?.salesAccTaxCertID, taxCertCountryRegionId, taxExemptNumber]);
    const vatNumComparison = useGetVatNumComparison(vatNumPayload);

    // Service Order Lines from D365 (D365 treats them as sales lines)
    const linesPayload: IReadVerbPayload = useMemo(() => ({
        keys: {
            dataAreaId: resolvedKeys?.dataAreaId ?? "",
            salesOrderNumber: resolvedKeys?.salesOrderNumber ?? "",
            serviceOrderID: orderID,
        },
    }), [resolvedKeys?.dataAreaId, resolvedKeys?.salesOrderNumber, orderID]);

    const { data: linesData, isLoading: linesLoading } = useGetSalesOrderLinesD365(linesPayload);

    // Line existence comparison (CS vs D365)
    const { data: existenceData } = useGetServiceOrderLineExistence(linesPayload);

    // Build a lookup: lineNumber → existence info (primary CS key)
    const existenceByLineNumber = useMemo(() => {
        const map = new Map<number, IServiceOrderLineExistenceInfo>();
        if (existenceData) {
            for (const item of existenceData) {
                if (item.lineNumber > 0) {
                    map.set(item.lineNumber, item);
                }
            }
        }
        return map;
    }, [existenceData]);

    // Build a lookup: inventoryLotId → existence info (D365 key)
    const existenceByLotId = useMemo(() => {
        const map = new Map<string, IServiceOrderLineExistenceInfo>();
        if (existenceData) {
            for (const item of existenceData) {
                if (item.inventoryLotId) {
                    map.set(item.inventoryLotId.toLowerCase(), item);
                }
            }
        }
        return map;
    }, [existenceData]);

    // Helper used both here and below: per-existence-row composite line key.
    const lineKeyOf = useCallback(
        (info: IServiceOrderLineExistenceInfo, ordinal: number) =>
            `${info.lineNumber}|${info.itemNumber}|${info.inventoryLotId}|${ordinal}`,
        [],
    );

    // RP keys: for each expanded existence row that maps to a D365 line, fetch the RP.
    const rpKeys = useMemo(() => {
        const result: { itemNumber: string; dataAreaId: string; lineNum: number }[] = [];
        const rows = existenceData ?? [];
        for (let ordinal = 0; ordinal < rows.length; ordinal++) {
            const info = rows[ordinal];
            if (!expandedServiceLineKeys.includes(lineKeyOf(info, ordinal))) {
                continue;
            }
            const d365Line = info.inventoryLotId
                ? (linesData ?? []).find((l) =>
                    l.inventoryLotId?.toLowerCase() === info.inventoryLotId.toLowerCase(),
                )
                : undefined;
            if (d365Line && d365Line.itemNumber && d365Line.dataAreaId) {
                result.push({
                    itemNumber: d365Line.itemNumber,
                    dataAreaId: d365Line.dataAreaId,
                    lineNum: info.lineNumber,
                });
            }
        }
        return result;
    }, [existenceData, expandedServiceLineKeys, linesData, lineKeyOf]);

    // Deduplicated RP keys for queries (one query per unique itemNumber|dataAreaId)
    const uniqueRPKeys = useMemo(() => {
        const seen = new Set<string>();
        return rpKeys.filter((k) => {
            const key = `${k.itemNumber}|${k.dataAreaId}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }, [rpKeys]);

    const releasedProductQueries = useQueries({
        queries: uniqueRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductComparison", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductComparison({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build a lookup map: "itemNumber|dataAreaId" → released product query result
    const releasedProductMap = useMemo(
        () => buildComparisonQueryMap(uniqueRPKeys, releasedProductQueries, (k) => `${k.itemNumber}|${k.dataAreaId}`),
        [uniqueRPKeys, releasedProductQueries],
    );

    // Product comparisons — one per unique productNumber
    const uniqueProductNumbers = useMemo(() => {
        const seen = new Set<string>();
        return rpKeys
            .map((k) => k.itemNumber)
            .filter((pn) => {
                if (!pn || seen.has(pn)) {
                    return false;
                }
                seen.add(pn);
                return true;
            });
    }, [rpKeys]);

    const productQueries = useQueries({
        queries: uniqueProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductComparison", { productNumber }],
            queryFn: () =>
                getProductComparison({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    // Build a lookup map: productNumber → product query result
    const productMap = useMemo(
        () => buildComparisonQueryMap(uniqueProductNumbers, productQueries, (pn) => pn),
        [uniqueProductNumbers, productQueries],
    );

    // D365-only product queries — for items where CS comparison returned nothing
    const buyProductNumbers = useMemo(() => {
        const seen = new Set<string>();
        return uniqueProductNumbers.filter((pn) => {
            const pQuery = productMap.get(pn);
            // Include if still loading or has no data — i.e. not confirmed as a "make" item
            if (pQuery?.isFetched && pQuery.data !== undefined) {
                return false;
            }
            if (seen.has(pn)) {
                return false;
            }
            seen.add(pn);
            return true;
        });
    }, [uniqueProductNumbers, productMap]);

    const buyProductQueries = useQueries({
        queries: buyProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductD365", { productNumber }],
            queryFn: () =>
                getProductD365({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyProductMap = useMemo(
        () => buildD365QueryMap<string, IProductD365>(buyProductNumbers, buyProductQueries, (pn) => pn),
        [buyProductNumbers, buyProductQueries],
    );

    // D365-only released product queries (for items without a CS comparison)
    const buyRPKeys = useMemo(() => {
        const seen = new Set<string>();
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        for (const k of uniqueRPKeys) {
            const mapKey = `${k.itemNumber}|${k.dataAreaId}`;
            if (seen.has(mapKey)) {
                continue;
            }
            const rpQuery = releasedProductMap.get(mapKey);
            if (!rpQuery?.isFetched || rpQuery.data === undefined) {
                seen.add(mapKey);
                keys.push({ itemNumber: k.itemNumber, dataAreaId: k.dataAreaId });
            }
        }
        return keys;
    }, [uniqueRPKeys, releasedProductMap]);

    const buyRPQueries = useQueries({
        queries: buyRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductD365", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductD365({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyRPMap = useMemo(
        () => buildD365QueryMap<{ itemNumber: string; dataAreaId: string }, IReleasedProductD365>(
            buyRPKeys,
            buyRPQueries,
            (k) => `${k.itemNumber}|${k.dataAreaId}`,
        ),
        [buyRPKeys, buyRPQueries],
    );

    // Store resolved keys once init is loaded
    useEffect(() => {
        if (init) {
            setResolvedKeys({
                orderID: init.serviceOrderID,
                salesOrderNumber: init.coServiceOrderID,
                dataAreaId: init.dataAreaId,
                custAccount: init.accRef,
                salesAccID: init.salesAccID,
                salesAccTaxCertID: init.salesAccTaxCertID,
                taxExemptNumber: init.taxExemptNumber,
                taxCertCountryRegionId: init.taxCertCountryRegionId,
                hasInterCompanyOrder: init.hasInterCompanyOrder,
            });
        }
    }, [init, setResolvedKeys]);

    const onNodeSelect = useCallback(
        (nodeId: string) => {
            const current = useExplorerStore.getState().selectedNodeId;
            setSelectedNodeId(nodeId === current ? null : nodeId);
        },
        [setSelectedNodeId],
    );

    const onLineKeySelect = useCallback(
        (lineKey: string) => {
            toggleServiceLineExpanded(lineKey);
        },
        [toggleServiceLineExpanded],
    );

    const onLineGroupSelect = useCallback(
        (groupKey: string) => {
            toggleServiceLineGroupExpanded(groupKey);
        },
        [toggleServiceLineGroupExpanded],
    );

    // Build graph
    const rawGraph = useMemo(() => {
        const headerStatus = computeStatus(
            headerComparison.data,
            headerComparison.isLoading,
            headerComparison.isError,
            headerComparison.isFetched,
        );

        // Customer Payments node (D365-only — empty list still renders)
        const paymentsRaw = customerPaymentsQuery.data ?? [];
        const paymentsItems = paymentsRaw.map((p) => ({
            voucher: p.voucher,
            transDate: p.transDate,
            amountCur: p.amountCur,
            currencyCode: p.currencyCode,
            accountNum: p.accountNum,
            dataAreaId: p.dataAreaId,
            companyBankAccountId: p.companyBankAccountId,
            createdBy1: p.createdBy1,
        }));
        const paymentsTotal = paymentsRaw.reduce((sum, p) => sum + (p.amountCur ?? 0), 0);
        const paymentsCurrency = paymentsRaw[0]?.currencyCode;
        const paymentsStatus: ExplorerNodeStatus = customerPaymentsQuery.isLoading
            ? "loading"
            : customerPaymentsQuery.isError
                ? "error"
                : "na";

        const customerPaymentsNode: ExplorerNode = {
            id: "CustomerPayments",
            type: "customerPaymentsNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "CustomerPayments",
                label: "Customer Payments",
                nodeTitle: "Customer Payments",
                nodeSubtitle: init?.coServiceOrderID ?? undefined,
                status: paymentsStatus,
                dataAreaId: init?.dataAreaId,
                summaryFields: [],
                isSelected: false,
                onSelect: onNodeSelect,
                source: "d365",
                payments: paymentsItems,
                paymentsTotal,
                paymentsCurrency,
            },
        };

        const soHeaderNode: ExplorerNode = {
            id: "ServiceOrderHeader",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "ServiceOrderHeader",
                label: init?.coServiceOrderID
                    ? `Service Order ${init.coServiceOrderID}`
                    : "Service Order",
                nodeTitle: "Service Order",
                nodeSubtitle: init?.coServiceOrderID ?? undefined,
                status: initLoading ? "loading" : headerStatus.status,
                dataAreaId: init?.dataAreaId,
                summaryFields: [
                    { label: "Contact", value: init?.contact ?? "—" },
                    { label: "Boat", value: init?.boat ?? "—" },
                ],
                orderStatus: salesOrderStatus ?? undefined,
                discrepancyCount: headerStatus.discrepancyCount,
                totalFields: headerStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: headerComparison.isFetched
                    ? (headerComparison.data !== undefined && !headerComparison.isError)
                    : null,
            },
        };

        // Customer node
        const customerStatus = computeStatus(
            customerComparison.data,
            customerComparison.isLoading,
            customerComparison.isError,
            customerComparison.isFetched,
        );

        const customerPartyNumber = customerComparison.data?.find(
            (d) => d.targetPropertyName === "PartyNumber",
        )?.targetValue as string | undefined;
        const customerOrgName = customerComparison.data?.find(
            (d) => d.targetPropertyName === "OrganizationName",
        )?.targetValue as string | undefined;
        const customerPartyType = customerComparison.data?.find(
            (d) => d.targetPropertyName === "PartyType",
        )?.targetValue as string | undefined;

        const customerNode: ExplorerNode = {
            id: "Customer",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "Customer",
                label: customerPartyNumber
                    ? `Customer ${customerPartyNumber}`
                    : "Customer",
                nodeTitle: "Customer",
                nodeSubtitle: customerPartyNumber ?? undefined,
                status: initLoading ? "loading" : customerStatus.status,
                dataAreaId: init?.dataAreaId,
                summaryFields: [
                    { label: "Organization", value: customerOrgName ?? "—" },
                    { label: "Party Type", value: customerPartyType ?? "—" },
                ],
                discrepancyCount: customerStatus.discrepancyCount,
                totalFields: customerStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: customerComparison.isFetched
                    ? (customerComparison.data !== undefined && !customerComparison.isError)
                    : null,
            },
        };

        // VatNum node (included only if there is a valid VAT number — service orders typically won't)
        const vatNumStatus = computeStatus(
            vatNumComparison.data,
            vatNumComparison.isLoading,
            vatNumComparison.isError,
            vatNumComparison.isFetched,
        );

        const vatNumNode: ExplorerNode = {
            id: "VatNum",
            type: "entityNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "VatNum",
                label: "VAT Number",
                nodeTitle: "VAT Number",
                status: initLoading ? "loading" : vatNumStatus.status,
                dataAreaId: init?.dataAreaId,
                summaryFields: [],
                discrepancyCount: vatNumStatus.discrepancyCount,
                totalFields: vatNumStatus.totalFields,
                isSelected: false,
                onSelect: onNodeSelect,
                d365Exists: vatNumComparison.isFetched
                    ? (vatNumComparison.data !== undefined && !vatNumComparison.isError)
                    : null,
            },
        };

        // Service Order Lines list node — drive from existenceData (spans CS+D365);
        // enrich each row with D365 line details when available.
        const d365LineByLot = new Map<string, typeof linesData extends (infer T)[] | undefined ? T : never>();
        const d365LineByLineNumber = new Map<number, typeof linesData extends (infer T)[] | undefined ? T : never>();
        for (const line of linesData ?? []) {
            if (line.inventoryLotId) {
                d365LineByLot.set(line.inventoryLotId.toLowerCase(), line);
            }
            if (line.lineNumber) {
                d365LineByLineNumber.set(line.lineNumber, line);
            }
        }

        // Composite key per existence row — service-order lineNumber alone is not unique
        // (a single CS service-order line can produce multiple D365 line "jobs").
        const makeLineKey = (info: IServiceOrderLineExistenceInfo, ordinal: number) =>
            `${info.lineNumber}|${info.itemNumber}|${info.inventoryLotId}|${ordinal}`;

        const lineItems = (existenceData ?? []).map((info, ordinal) => {
            const d365Line = info.inventoryLotId
                ? d365LineByLot.get(info.inventoryLotId.toLowerCase())
                : info.lineNumber
                    ? d365LineByLineNumber.get(info.lineNumber)
                    : undefined;
            const lineKey = makeLineKey(info, ordinal);
            return {
                lineKey,
                lineNumber: info.lineNumber,
                itemNumber: info.itemNumber,
                externalItemNumber: d365Line?.externalItemNumber ?? "",
                lineDescription: d365Line?.lineDescription || d365Line?.lineName || info.itemNumber,
                quantity: d365Line?.orderedSalesQuantity ?? 0,
                salesOrderLineStatus: d365Line?.salesOrderLineStatus ?? "",
                isExpanded: expandedServiceLineKeys.includes(lineKey),
                existsInCS: info.existsInCS,
                existsInD365: info.existsInD365,
            };
        });

        // Group lineItems by externalItemNumber. For service orders, multiple D365 line "jobs"
        // can share a single externalItemNumber (one per CS LineID).
        const groupMap = new Map<string, typeof lineItems>();
        for (const item of lineItems) {
            // Group label preference: D365's externalItemNumber when present; otherwise itemNumber.
            const groupLabel = item.externalItemNumber || item.itemNumber || `Line ${item.lineNumber}`;
            const existing = groupMap.get(groupLabel);
            if (existing) {
                existing.push(item);
            } else {
                groupMap.set(groupLabel, [item]);
            }
        }

        const lineGroups = Array.from(groupMap.entries()).map(([groupKey, items]) => ({
            groupKey,
            externalItemNumber: groupKey,
            isExpanded: expandedServiceLineGroups.includes(groupKey),
            existsInCS: items.some((i) => i.existsInCS),
            existsInD365: items.some((i) => i.existsInD365),
            items,
        }));

        const linesNode: ExplorerNode = {
            id: "ServiceOrderLines",
            type: "serviceOrderLinesNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "ServiceOrderLines",
                label: "Service Order Lines",
                nodeTitle: "Service Order Lines",
                nodeSubtitle: init?.coServiceOrderID ?? undefined,
                dataAreaId: init?.dataAreaId,
                status: linesLoading ? "loading" : (lineItems.length > 0 ? "synced" : "na"),
                summaryFields: [],
                lineGroups,
                onLineKeySelect,
                onLineGroupSelect,
                isSelected: false,
                onSelect: onNodeSelect,
            },
        };

        // Line detail nodes for expanded lines (keyed by composite lineKey).
        const lineDetailNodes: ExplorerNode[] = lineItems
            .filter((item) => item.isExpanded)
            .map((item) => {
                const info = (existenceData ?? []).find((_, idx) =>
                    makeLineKey((existenceData ?? [])[idx], idx) === item.lineKey,
                );
                if (!info) {
                    return null;
                }
                const d365Line = info.inventoryLotId
                    ? d365LineByLot.get(info.inventoryLotId.toLowerCase())
                    : d365LineByLineNumber.get(info.lineNumber);

                const nodeId = `ServiceOrderLineDetail-${item.lineKey}`;
                return {
                    id: nodeId,
                    type: "lineDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ServiceOrderLineDetail" as const,
                        label: `Service Order Line ${d365Line?.externalItemNumber || info.itemNumber}`,
                        nodeTitle: "Service Order Line",
                        nodeSubtitle: d365Line?.externalItemNumber || info.itemNumber,
                        status: "synced" as const,
                        summaryFields: [],
                        lineDetail: {
                            lineKey: item.lineKey,
                            lineNumber: info.lineNumber,
                            status: d365Line?.salesOrderLineStatus ?? "",
                            itemNumber: info.itemNumber,
                            externalItemNumber: d365Line?.externalItemNumber ?? "",
                            lineDescription: d365Line?.lineDescription || d365Line?.lineName || info.itemNumber,
                            quantity: d365Line?.orderedSalesQuantity ?? 0,
                            salesUnitSymbol: d365Line?.salesUnitSymbol ?? "",
                            salesPrice: d365Line?.salesPrice ?? 0,
                            lineAmount: d365Line?.lineAmount ?? 0,
                            currencyCode: d365Line?.currencyCode ?? "",
                            inventoryLotId: info.inventoryLotId,
                            dataAreaId: init?.dataAreaId ?? "",
                            orderID: orderID,
                            existsInCS: info.existsInCS,
                            existsInD365: info.existsInD365,
                        },
                        isSelected: false,
                        onSelect: onNodeSelect,
                    },
                } as ExplorerNode;
            })
            .filter((n): n is ExplorerNode => n !== null);

        const lineDetailEdges: ExplorerEdge[] = lineDetailNodes.map((n) => ({
            id: `e-line-${n.id}`,
            source: "ServiceOrderLines",
            target: n.id,
        }));

        // Group expanded lines by productNumber for shared Product + ReleasedProduct nodes.
        // For service orders we group by the D365 ItemNumber resolved per existence row.
        const productLineMap = new Map<string, string[]>();
        const expandedItems = lineItems.filter((item) => item.isExpanded);
        for (const item of expandedItems) {
            // Resolve the D365 ItemNumber for this row (preferring the matched D365 line).
            const matchedD365 = item.itemNumber; // existence ItemNumber matches D365 ItemNumber when paired
            if (!matchedD365) {
                continue;
            }
            const existing = productLineMap.get(matchedD365);
            if (existing) {
                existing.push(item.lineKey);
            } else {
                productLineMap.set(matchedD365, [item.lineKey]);
            }
        }

        // Product nodes — one per unique ProductNumber, connected from line details
        const productNodes: ExplorerNode[] = [];
        const productEdges: ExplorerEdge[] = [];

        for (const [productNumber, lineNums] of productLineMap) {
            const pQuery = productMap.get(productNumber);
            const buyQuery = buyProductMap.get(productNumber);

            let pStatus: { status: ExplorerNodeStatus; discrepancyCount: number; totalFields: number };
            let d365Exists: boolean | null | undefined;

            if (pQuery?.isFetched && pQuery.data !== undefined) {
                // CS comparison available
                pStatus = computeStatus(pQuery.data, pQuery.isLoading, pQuery.isError, pQuery.isFetched);
                d365Exists = pQuery.isFetched ? (pQuery.data !== undefined && !pQuery.isError) : null;
            } else if (buyQuery) {
                // D365-only
                pStatus = buyQuery.isLoading
                    ? { status: "loading" as const, discrepancyCount: 0, totalFields: 0 }
                    : buyQuery.isError
                        ? { status: "error" as const, discrepancyCount: 0, totalFields: 0 }
                        : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                d365Exists = buyQuery.data ? true : buyQuery.isError ? false : undefined;
            } else {
                pStatus = { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                d365Exists = undefined;
            }

            const nodeId = `Product-${productNumber}`;
            productNodes.push({
                id: nodeId,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Product" as const,
                    label: `Product ${productNumber}`,
                    nodeTitle: "Product",
                    nodeSubtitle: productNumber,
                    status: pStatus.status,
                    summaryFields: [
                        { label: "Product Number", value: productNumber },
                    ],
                    discrepancyCount: pStatus.discrepancyCount,
                    totalFields: pStatus.totalFields,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists,
                },
            } as ExplorerNode);

            // Edges from each line detail → Product
            for (const lineKey of lineNums) {
                productEdges.push({
                    id: `e-prod-${lineKey}`,
                    source: `ServiceOrderLineDetail-${lineKey}`,
                    target: nodeId,
                });
            }
        }

        // Released Product nodes — one per unique itemNumber|dataAreaId (no list/fan-out for service orders)
        const releasedProductNodes: ExplorerNode[] = [];
        const releasedProductEdges: ExplorerEdge[] = [];

        for (const [productNumber] of productLineMap) {
            const line = linesData?.find((l) => l.itemNumber === productNumber);
            const dataAreaId = line?.dataAreaId || init?.dataAreaId || "";
            const rpNodeId = `ReleasedProduct-${productNumber}|${dataAreaId}`;
            const mapKey = `${productNumber}|${dataAreaId}`;
            const rpQuery = releasedProductMap.get(mapKey);
            const buyRPQuery = buyRPMap.get(mapKey);

            let rpStatus: ExplorerNodeStatus;
            let d365Exists: boolean | null | undefined;

            if (rpQuery?.isFetched && rpQuery.data !== undefined) {
                const rpStatusResult = computeStatus(rpQuery.data, rpQuery.isLoading, rpQuery.isError, rpQuery.isFetched);
                rpStatus = rpStatusResult.status;
                d365Exists = rpQuery.isFetched ? (rpQuery.data !== undefined && !rpQuery.isError) : null;
            } else if (rpQuery?.isLoading) {
                rpStatus = "loading";
                d365Exists = null;
            } else if (buyRPQuery) {
                rpStatus = buyRPQuery.isLoading ? "loading" : buyRPQuery.isError ? "error" : "na";
                d365Exists = buyRPQuery.data ? true : buyRPQuery.isError ? false : undefined;
            } else {
                rpStatus = "na";
                d365Exists = undefined;
            }

            releasedProductNodes.push({
                id: rpNodeId,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "ReleasedProduct" as const,
                    label: "Released Product",
                    nodeTitle: "Released Product",
                    nodeSubtitle: productNumber,
                    status: rpStatus,
                    dataAreaId,
                    summaryFields: [
                        { label: "Product Number", value: productNumber },
                        { label: "Company", value: dataAreaId },
                    ],
                    discrepancyCount: 0,
                    totalFields: 0,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists,
                },
            } as ExplorerNode);

            releasedProductEdges.push({
                id: `e-rp-${productNumber}`,
                source: `Product-${productNumber}`,
                target: rpNodeId,
            });
        }

        // Expanded Released Product entity nodes
        const expandedRPNodes: ExplorerNode[] = [];
        const expandedRPEdges: ExplorerEdge[] = [];

        for (const rpKey of expandedReleasedProducts) {
            const [itemNumber, dataAreaId] = rpKey.split("|");
            if (!itemNumber || !dataAreaId) {
                continue;
            }

            const mapKey = rpKey;
            const rpQuery = releasedProductMap.get(mapKey);
            const rpStatus = rpQuery
                ? computeStatus(rpQuery.data, rpQuery.isLoading, rpQuery.isError, rpQuery.isFetched)
                : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };

            const rpNodeId = `ReleasedProduct-${rpKey}`;
            const rpCompData = rpQuery?.data;
            const rpProductionType = rpCompData?.find((f) => f.sourcePropertyName === "ProductionType")?.sourceValue ?? null;

            // Find the parent RP node (the non-expanded entity node for this item)
            const parentRPNode = releasedProductNodes.find((n) => n.id === rpNodeId);
            if (!parentRPNode) {
                continue;
            }

            expandedRPNodes.push({
                id: `${rpNodeId}-expanded`,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "ReleasedProduct" as const,
                    label: "Released Product",
                    nodeTitle: "Released Product",
                    nodeSubtitle: itemNumber,
                    status: rpStatus.status,
                    dataAreaId,
                    summaryFields: [
                        { label: "Production Type", value: rpProductionType as string | null },
                    ],
                    discrepancyCount: rpStatus.discrepancyCount,
                    totalFields: rpStatus.totalFields,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists: rpQuery?.isFetched
                        ? (rpQuery.data !== undefined && !rpQuery.isError)
                        : null,
                },
            } as ExplorerNode);

            expandedRPEdges.push({
                id: `e-rpexp-${rpKey}`,
                source: parentRPNode.id,
                target: `${rpNodeId}-expanded`,
            });
        }

        const fullIdPrefixes = ["ServiceOrderLineDetail-", "ReleasedProduct-", "Product-"];
        const allNodes: ExplorerNode[] = [
            soHeaderNode, customerPaymentsNode, customerNode,
            ...(hasValidVatNum ? [vatNumNode] : []),
            linesNode, ...lineDetailNodes, ...productNodes, ...releasedProductNodes,
            ...expandedRPNodes,
        ].map((n) => ({
            ...n,
            dragHandle: ".node-drag-handle",
            data: {
                ...n.data,
                selectId: fullIdPrefixes.some((p) => n.id.startsWith(p)) ? n.id : n.data.entityType,
            },
        }));
        const allEdges: ExplorerEdge[] = [
            { id: "e-customer-payments", source: "CustomerPayments", target: "ServiceOrderHeader" },
            { id: "e-customer", source: "Customer", target: "ServiceOrderHeader" },
            ...(hasValidVatNum ? [{ id: "e-vatnum", source: "VatNum", target: "ServiceOrderHeader" }] : []),
            { id: "e-lines", source: "ServiceOrderHeader", target: "ServiceOrderLines" },
            ...lineDetailEdges,
            ...productEdges,
            ...releasedProductEdges,
            ...expandedRPEdges,
        ];

        return { nodes: allNodes, edges: allEdges };
    }, [
        init,
        initLoading,
        salesOrderStatus,
        orderID,
        headerComparison.data,
        headerComparison.isLoading,
        headerComparison.isError,
        headerComparison.isFetched,
        customerPaymentsQuery.data,
        customerPaymentsQuery.isLoading,
        customerPaymentsQuery.isError,
        customerComparison.data,
        customerComparison.isLoading,
        customerComparison.isError,
        customerComparison.isFetched,
        vatNumComparison.data,
        vatNumComparison.isLoading,
        vatNumComparison.isError,
        vatNumComparison.isFetched,
        linesData,
        linesLoading,
        existenceByLineNumber,
        existenceByLotId,
        releasedProductMap,
        productMap,
        buyProductMap,
        buyRPMap,
        hasValidVatNum,
        expandedServiceLineKeys,
        expandedServiceLineGroups,
        expandedReleasedProducts,
        onNodeSelect,
        onLineKeySelect,
        onLineGroupSelect,
    ]);

    // Unified node state — ReactFlow owns positions, dimensions, selection via applyNodeChanges
    const [nodesState, setNodesState] = useState<ExplorerNode[]>([]);
    const prevStructureKeyRef = useRef<string>("");

    // Structure fingerprint — node/edge IDs plus the dynamic-height inputs of nodes whose
    // dagre height isn't fixed (CustomerPayments grows with payment count). Keeping this
    // in the key forces a re-layout when payments load asynchronously.
    const structureKey = useMemo(() => {
        const nk = rawGraph.nodes.map((n) => n.id).sort().join(",");
        const ek = rawGraph.edges.map((e) => e.id).sort().join(",");
        const paymentsNode = rawGraph.nodes.find((n) => n.id === "CustomerPayments");
        const paymentCount = paymentsNode?.data.payments?.length ?? 0;
        return `${nk}|${ek}|p${paymentCount}`;
    }, [rawGraph.nodes, rawGraph.edges]);

    // Re-run dagre layout only when the graph structure changes (nodes added/removed)
    // Preserves drag positions for nodes that already existed
    useEffect(() => {
        if (structureKey === prevStructureKeyRef.current) {
            return;
        }
        prevStructureKeyRef.current = structureKey;
        const { nodes: layouted } = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
        setNodesState((prev) => {
            // On structure change, accept dagre's fresh positions for all nodes — preserving
            // old positions wholesale here would freeze the layout when a node's dagre height
            // changes (e.g., CustomerPayments growing as payments load) and break spacing.
            // Only preserve ReactFlow's measured dimensions to avoid a flicker.
            const prevMap = new Map(prev.map((n) => [n.id, n]));
            return layouted.map((n) => ({
                ...n,
                measured: prevMap.get(n.id)?.measured ?? n.measured,
            }));
        });
    }, [structureKey, rawGraph.nodes, rawGraph.edges]);

    // Merge latest data from rawGraph onto nodesState (preserves positions, dimensions, etc.)
    // Also apply isSelected here instead of inside rawGraph to avoid recomputing the entire
    // graph on every selection change.
    const nodes = useMemo(() => {
        const applySelection = (n: ExplorerNode): ExplorerNode => ({
            ...n,
            data: { ...n.data, isSelected: selectedNodeId === n.id },
        });
        if (nodesState.length === 0) {
            // First render before effect fires — compute layout inline
            const result = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
            return result.nodes.map(applySelection);
        }
        const stateMap = new Map(nodesState.map((n) => [n.id, n]));
        return rawGraph.nodes.map((n) => {
            const merged = stateMap.has(n.id)
                ? { ...stateMap.get(n.id)!, data: n.data, dragHandle: n.dragHandle }
                : n;
            return applySelection(merged);
        });
    }, [rawGraph.nodes, rawGraph.edges, nodesState, selectedNodeId]);

    const edges = rawGraph.edges;

    // Apply ALL ReactFlow changes (positions, dimensions, selection) to nodesState
    const onNodesChange = useCallback((changes: NodeChange<ExplorerNode>[]) => {
        setNodesState((prev) => applyNodeChanges(changes, prev));
    }, []);

    const comparisonDataMap = useMemo(
        () => {
            const map: Record<string, IComparisonInfo[] | undefined> = {
                ServiceOrderHeader: headerComparison.data,
                Customer: customerComparison.data,
                ...(hasValidVatNum ? { VatNum: vatNumComparison.data } : {}),
            };
            // Add per-item released product comparisons (keyed by "itemNumber|dataAreaId")
            for (const [rpKey, rpQuery] of releasedProductMap) {
                map[`ReleasedProduct-${rpKey}`] = rpQuery.data;
            }
            // Add per-productNumber product comparisons
            for (const [productNumber, pQuery] of productMap) {
                map[`Product-${productNumber}`] = pQuery.data;
            }
            return map;
        },
        [headerComparison.data, customerComparison.data, hasValidVatNum, vatNumComparison.data, releasedProductMap, productMap],
    );

    return {
        nodes,
        edges,
        onNodesChange,
        isInitLoading: initLoading,
        comparisonDataMap,
        existenceData,
    };
}
